import { MapPin, X, Crosshair } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useGeo } from "@/context/GeoContext";

/**
 * Phase 8M.1 — chip is ~40 % smaller (font 11.5 → 9.5, padding tightened)
 * and the GPS button that used to live on the chat composer's left edge has
 * been folded into this chip: the whole IP-only chip is a button that asks
 * for precise location, with a small target glyph at the front.
 */
export default function NearMeChip() {
    const { userGeo, clearUserGeo, geo, geoConsent, requestPreciseLocation, loading } = useGeo();
    const { t } = useTranslation();

    if (userGeo) {
        const cc = userGeo.country_code || "";
        const city = userGeo.city || "your area";
        const pinpoint = userGeo.formatted_short && userGeo.formatted_short.startsWith("near ")
            ? userGeo.formatted_short
            : null;
        return (
            <div
                className="flex flex-col items-center justify-center gap-0.5 pb-1.5"
                data-testid="near-me-chip-wrap"
            >
                <span
                    className="inline-flex items-center gap-1 rounded-full border border-[#0A0A0A] bg-[#0A0A0A] px-[7px] py-[2px] text-[10px] font-medium text-white"
                    data-testid="near-me-chip"
                >
                    <MapPin size={9} strokeWidth={2.25} />
                    <span data-testid="near-me-chip-text">
                        {pinpoint || `Near ${city}${cc ? `, ${cc}` : ""}`}
                    </span>
                    <button
                        type="button"
                        onClick={clearUserGeo}
                        aria-label="Clear my location"
                        data-testid="near-me-chip-clear"
                        className="ml-0.5 inline-flex h-3 w-3 items-center justify-center rounded-full text-white/80 transition hover:bg-white/15 hover:text-white"
                    >
                        <X size={8} strokeWidth={2.5} />
                    </button>
                </span>
            </div>
        );
    }

    // IP-derived chip (no GPS pinned). While IP geo is still resolving
    // (cold-start window, especially on iOS WebView) render a "Detecting…"
    // pill so the chip slot never collapses to nothing — the previous
    // null-return left an empty space and made the location feature feel
    // broken.
    if (!geo?.country_code) {
        if (!loading) return null;
        return (
            <div
                className="flex flex-col items-center justify-center gap-0.5 pb-1.5"
                data-testid="near-me-chip-wrap"
            >
                <span
                    className="inline-flex items-center gap-1 rounded-full border border-[#E5E5E5] bg-white px-[7px] py-[2px] text-[10px] text-[#9CA3AF]"
                    data-testid="near-me-chip-detecting"
                >
                    <Crosshair size={9} strokeWidth={2.25} className="text-[#9CA3AF]" />
                    <span>Detecting your location…</span>
                </span>
            </div>
        );
    }

    const country = geo.country_code;
    const ipLabel = geo.city || country;
    return (
        <div
            className="flex flex-col items-center justify-center gap-0.5 pb-1.5"
            data-testid="near-me-chip-wrap"
        >
            <button
                type="button"
                onClick={requestPreciseLocation}
                aria-label={t("geo.use_precise")}
                className="inline-flex items-center gap-1 rounded-full border border-[#E5E5E5] bg-white px-[7px] py-[2px] text-[10px] text-[#374151] transition hover:border-[#0A0A0A]"
                data-testid="near-me-chip-ip"
            >
                <Crosshair size={9} strokeWidth={2.25} className="text-[#0A0A0A]" />
                <span data-testid="near-me-chip-text">
                    Near {ipLabel}
                </span>
                <span className="italic text-[#9CA3AF]" data-testid="near-me-chip-ip-suffix">
                    (IP)
                </span>
            </button>
            {geoConsent === "denied" && (
                <button
                    type="button"
                    onClick={requestPreciseLocation}
                    className="text-[10px] text-[#0A0A0A] underline underline-offset-2 transition hover:opacity-70"
                    data-testid="use-precise-location"
                >
                    {t("geo.use_precise")}
                </button>
            )}
        </div>
    );
}
